import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Observable, Subscriber, Subscription } from 'rxjs';
import { Counter } from '../../models/counter';
import { AreaItem } from '../../models/area-item';
import { ProductPack } from '../../models/product-pack';
import { ProductTimeSpan } from '../../models/product-time-span';
import { UpDownComponent } from '../up-down/up-down.component';
import { Order } from '../../../order/models/order';
import { OrderLine } from '../../../order/models/order-line';
import { OrderLineType } from '../../../order/models/order-line-type';
import { SingleOrdersCache } from '../../models/single-orders-cache';
import { ILabelWithIconData } from '../label-with-icon/label-with-icon.component';
import { TimeStampsCache } from '../../models/time-stamps-cache';
import { groupBy } from '../../models/utils';
import { SingleBookingCache } from '../../models/single-booking-cache';
import { BookingOrderService } from '../../services/booking-order.service';
import { environment } from '../../../../../environments/environment';
import { ShopSettingsService } from '../../../general/services/shop-settings.service';
import { Product } from '../../../order/models/product';
import { TranslateService } from '@ngx-translate/core';
import { faCaretLeft, faCaretRight } from '@fortawesome/free-solid-svg-icons';
import { OrderStateService } from '../../../order/services/order-state.service';

function createLabelWithIconData(text: string, iconCssClass: string): ILabelWithIconData {
  return {
    text: text,
    iconCssClass: iconCssClass,
  };
}

const labelWithIconData = {
  'wait': createLabelWithIconData('Bitte warten ...', 'fa fa-clock-o'),
  'empty': createLabelWithIconData('Keine Tickets sind verfügbar', 'fa fa-thumbs-o-down'),
};

@Component({
  selector: 'app-ticket-configurator',
  templateUrl: 'ticket-configurator.component.html',
  styleUrls: ['ticket-configurator.component.scss']
})
export class TicketConfiguratorComponent implements OnInit, OnDestroy {
  private cache: SingleBookingCache;
  private areaItems: AreaItem[] = [];
  protected _productPacks: ProductPack[] = [];
  protected _labelWithIconData = labelWithIconData['wait'];
  countUpEnabled: boolean;
  protected totalSingleCountMax: number;
  protected _errorGroupName = '';
  protected readonly singleProductIds: Set<number> = new Set<number>();
  protected readonly minutesToMaxCount: Map<number, number> = new Map<number, number>();
  protected readonly counterToMinutes: Map<number, number> = new Map<number, number>();

  protected readonly groupToMinValue: Map<string, number> = new Map<string, number>();
  protected readonly groupToMaxValue: Map<string, number> = new Map<string, number>();
  protected readonly counterToGroup: Map<number, string> = new Map<number, string>();

  onLangChange: Subscription;
  bookingOrderChange: Subscription;

  @Input() productPacksPerPage = 6;
  currentPageIndex = 0;

  faCaretLeft = faCaretLeft;
  faCaretRight = faCaretRight;

  constructor(
    private bookingOrderService: BookingOrderService,
    private shopSettingsService: ShopSettingsService,
    private translateService: TranslateService,
    private orderStateService: OrderStateService,
  ) {
    this.cache = bookingOrderService.singleCache;
  }

  ngOnInit() {
    this.initialize();
    this.bookingOrderChange = this.bookingOrderService.messageChange.subscribe(() => {
      this.initialize();
    });

    this.onLangChange = this.translateService.onLangChange.subscribe(() => {
      this.updateInvalidReason();
    });


  }

  initialize() {
    this.bookingOrderService.singleCache.ordersCache.updateCacheOrdersFromServiceOrder();

    const areaItems = this.bookingOrderService.areaItems;
    this.singleProductIds.clear();
    this.minutesToMaxCount.clear();
    this.counterToMinutes.clear();

    this._labelWithIconData = areaItems && areaItems.length ? null : labelWithIconData['empty'];
    this.updateInvalidReason();
    this.areaItems = areaItems;
    this.cache.timeStampsCache.updateTimeSpans(areaItems);

    this.updateProductPacks(null);
    this.updateNotSelectedTimeSpans();
    this.selectAvailableTimeSpan();
  }

  ngOnDestroy(): void {
    if (this.onLangChange) {
      this.onLangChange.unsubscribe();
      this.onLangChange = null;
    }

    if (this.bookingOrderChange) {
      this.bookingOrderChange.unsubscribe();
      this.bookingOrderChange = null;
    }
  }

  get selectedProductTimeSpan(): ProductTimeSpan {
    return this.bookingOrderService.singleCache.timeStampsCache.selectedProductTimeSpan;
  }

  get productTimeSpans(): ProductTimeSpan[] {
    return this.bookingOrderService.singleCache.timeStampsCache.productTimeSpans;
  }

  private selectAvailableTimeSpan() {
    const availableTimeStamps = this.bookingOrderService.singleCache.timeStampsCache.productTimeSpans.filter(x => x.isCapacityAvailable);
    if (availableTimeStamps.length) {
      this.updateProductPacks(availableTimeStamps[0]);
    }
  }

  productTimeSpanSelectionChanged(productTimeSpan: ProductTimeSpan) {
    this.updateProductPacks(productTimeSpan);
  }

  private updateNotSelectedTimeSpans() {
    const productTimeSpan = this.selectedProductTimeSpan;
    if (productTimeSpan == null) {
      return;
    }

    const selectedOrder = this.cache.ordersCache.getCachedOrder(productTimeSpan.timeSpan);
    if (selectedOrder == null) {
      return;
    }

    const keys = this.cache.ordersCache.cachedKeys;
    for (const key of keys) {
      const order = this.cache.ordersCache.getCachedOrder(key);
      if (order === selectedOrder) {
        continue;
      }

      const pts = this.cache.timeStampsCache.productTimeSpans.filter(x => x.timeSpan === key);
      if (pts.length) {
        this.updateTimeSpan(
          order,
          pts[0]
        ).subscribe(
          done => {
            this.updateInvalidReason();
          }
        );
      }
    }
  }

  private updateTimeSpan(order: Order, productTimeSpan: ProductTimeSpan): Observable<boolean> {
    const orderLineType = OrderLineType.SingleProduct;

    return Observable.create(
      (observable: Subscriber<boolean>) => {
        this.bookingOrderService.isBookingSlotCapacityValid(
          productTimeSpan.timeSpan,
          order == null ? 1 : order.getQuantity(orderLineType))
          .subscribe(
            isCapacityAvailable => {

              const amount = order == null ? 0 : order.getAmount(orderLineType);
              productTimeSpan.amount = amount;
              productTimeSpan.isCapacityAvailable = isCapacityAvailable;

              observable.next();
            }
          );
      });

  }

  get availableAreaItems(): AreaItem[] {
    const areaItems = this.areaItems;
    if (!areaItems || areaItems.length === 0) {
      return [];
    }

    if (this.bookingOrderService.isEventSlotOrder) {
      return areaItems;
    }

    const date = this.bookingOrderService.selectedBookingDate;
    const bookingSlots = this.bookingOrderService.selectedBookingSlots;

    return this.bookingOrderService.filterAreaItemsByBookingSlots(areaItems, date, bookingSlots);
  }

  private updateProductPacks(
    productTimeSpan: ProductTimeSpan
  ) {
    if (!productTimeSpan) {
      productTimeSpan = this.selectedProductTimeSpan;
    }

    const areaItems = this.availableAreaItems;
    if (areaItems == null || areaItems.length === 0 || productTimeSpan == null) {
      this._productPacks = [];
      return;
    }

    const singleTicketsMaximumQuantity = this.shopSettingsService.shopSettings.singleTicketsMaximumQuantity;
    const singleTicketsMaximumQuantityGlobalSettings = environment.globalSettings.singleMaxPersonCount;

    this.totalSingleCountMax = singleTicketsMaximumQuantity && singleTicketsMaximumQuantity > 0 ? singleTicketsMaximumQuantity : singleTicketsMaximumQuantityGlobalSettings;

    const timeSpan = productTimeSpan.timeSpan;

    const order = this.cache.ordersCache.getOrCreateCachedOrder(timeSpan);

    this.bookingOrderService.singleCache.timeStampsCache.selectedProductTimeSpan = productTimeSpan;

    this.updateTimeSpan(
      order,
      productTimeSpan
    ).subscribe(
      done => {

        this.countUpEnabled = productTimeSpan.isCapacityAvailable;

        this._productPacks = [];

        const areaItemsByTimeSpan = areaItems; // .filter(x => x.minutes === productTimeSpan.timeSpan);
        const groups = groupBy(areaItemsByTimeSpan, 'group');
        for (const key of Object.keys(groups)) {
          const value = groups[key];
          if (key === 'null' || key === 'false') {
            for (const item of value) {
              this._productPacks.push(this.fromAreaItems(order, [item]));
            }
          } else {
            this._productPacks.push(this.fromAreaItems(order, value));
          }
        }

        if (this._productPacks) {
          this._productPacks.sort((a, b) => a.sequence - b.sequence);
        }
        this.updateProductPackMaxCount();

        this.cache.ordersCache.updateServiceOrderLinesFromCache(productTimeSpan.timeSpan, true);
        this.updateInvalidReason();
      });
  }

  get selectedBookingSlotsAvailableCapacity(): number {
    if (!this.bookingOrderService.selectedBookingSlots) {
      return 0;
    }

    return Math.min(...this.bookingOrderService.selectedBookingSlots.map(item => item.freeQuantity));
  }

  private fromAreaItems(order: Order, areaItems: AreaItem[]): ProductPack {
    let productPack: ProductPack = null;
    const counters: Counter[] = [];

    const products = this.getProducts(areaItems);

    for (const item of areaItems) {
      if (productPack === null) {
        const name = item.group ? item.group : item.name;
        let highestProductSequence = 1;
        for (const product of products) {
          if (product.sequence > highestProductSequence) {
            highestProductSequence = product.sequence;
          }
        }
        productPack = new ProductPack(name, item.description, item.priceDescription, counters, this.buildUrl(item.productId), item.productDescription, products, highestProductSequence);
      }
      this.buildCounter(order, item, productPack);
    }

    return productPack;
  }

  buildUrl(productId: number): string {
    if (this.shopSettingsService.shopSettings.isShowProductImages) {
      return `/store/product_image/${productId}`;
    }
    return null;
  }

  getOrCreateOrderLine(order: Order, item: AreaItem) {
    const productId = item.productId;
    let orderLine = order.getOrderLineByProductId(productId);
    if (orderLine) {
      return orderLine;
    }

    orderLine = order.addOrderLine(
      OrderLineType.SingleProduct,
      item.productName,
      item.productId,
      item.price,
      item.taxRate,
      0,
      true);

    orderLine.isStockProduct = item.isStockProduct;

    return orderLine;
  }

  private buildCounter(order: Order, item: AreaItem, productPack: ProductPack) {

    const counterName = item.group ? item.name : '';

    const orderLine = this.getOrCreateOrderLine(order, item);

    const itemQuantity = item.itemQuantity ? item.itemQuantity : 1;
    let count = orderLine.quantity / itemQuantity;
    if (item.isGroupBooking) {
      count = orderLine.groupSize | 0;
    }

    let countMax = item.availableMaxQuantity(this.bookingOrderService.selectedPickupPointGroupId);
    const areaItemAvailableQuantity = this.areaItemAvailableQuantity(item);
    if (countMax || item.isStockProduct) {
      if (item.isStockProduct) {
        const productCountInOrders = this.bookingOrderService.order.getReservedProductCount(
          item.productId,
          this.bookingOrderService.selectedBookingDate,
          this.bookingOrderService.selectedPickupPointGroupId);
        countMax -= productCountInOrders;
        if (countMax < 0) {
          countMax = 0;
        }
      }
      countMax = Math.min(countMax, areaItemAvailableQuantity);
    } else {
      countMax = areaItemAvailableQuantity;
    }

    let countMin = item.isGroupBooking ? item.groupSizeMin : item.ticketCountMin;
    if (item.group) {
      let min = countMin;
      if (this.groupToMinValue.has(item.group)) {
        min = Math.min(this.groupToMinValue.get(item.group), min);
      }
      this.groupToMinValue.set(item.group, min);

      let max = countMax;
      if (this.groupToMaxValue.has(item.group)) {
        max = Math.max(this.groupToMaxValue.get(item.group), max);
      }
      this.groupToMaxValue.set(item.group, max);

      countMin = 0;
      this.counterToGroup.set(item.id, item.group);
    }

    const counter = new Counter(
      item.id,
      count,
      counterName,
      orderLine,
      countMax,
      countMin,
    );

    if (item.groupSizeMax <= 0 && item.ticketCountMax <= 0) {
      this.singleProductIds.add(item.id);
    }

    let quantity = areaItemAvailableQuantity;
    if (this.minutesToMaxCount.has(item.minutes)) {
      quantity = Math.min(quantity, this.minutesToMaxCount.get(item.minutes));
    }
    this.minutesToMaxCount.set(item.minutes, quantity);

    this.counterToMinutes.set(item.id, item.minutes);

    productPack.addCounter(counter);
  }

  areaItemAvailableQuantity(areaItem: AreaItem): number {
    if (!areaItem || !this.bookingOrderService.selectedBookingSlots || this.bookingOrderService.selectedBookingSlots.length === 0) {
      return 0;
    }

    const bookingSlot = this.bookingOrderService.selectedBookingSlots[0];
    if (!bookingSlot || !bookingSlot.bookedDetailQuantity || bookingSlot.bookedDetailQuantity.length === 0) {
      return bookingSlot.freeQuantity;
    }

    for (const bookedQuantity of bookingSlot.bookedDetailQuantity) {
      if (bookedQuantity && bookedQuantity.areaItemId === areaItem.id) {
        return bookedQuantity.availableQuantity;
      }
    }

    return bookingSlot.freeQuantity;
  }

  getProducts(areaItems: AreaItem[]): Product[] {
    if (!areaItems) {
      return null;
    }

    return areaItems.map(areaItem =>
      new Product(
        areaItem.productId,
        areaItem.productName,
        areaItem.price,
        areaItem.taxRate,
        this.buildUrl(areaItem.productId),
        areaItem.productDescription,
        areaItem.productPriceSchedules,
        areaItem.sequence,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        areaItem.productSaleDescription,
      )
    );
  }

  onCounterChanged(counter) {
    const orderLineType = OrderLineType.SingleProduct;
    const orderLine = counter.tag as OrderLine;

    if (counter != null && orderLine != null) {
      const areaItemTemp = this.areaItems.find(a => a.productId === orderLine.productId);
      const itemQuantity = areaItemTemp.itemQuantity ? areaItemTemp.itemQuantity : 1;
      const count = counter.count;
      let qty = count * itemQuantity;
      if (areaItemTemp.isGroupBooking) {
        qty = count === 0 ? 0 : 1;
        orderLine.groupSize = count;
      }
      const price = this.bookingOrderService.computePriceByAreaItem(areaItemTemp, qty);
      orderLine.quantity = qty;
      orderLine.price = price;
    }

    this.updateProductPackMaxCount();

    const productTimeSpan = this.selectedProductTimeSpan;
    if (!productTimeSpan) {
      return;
    }

    const order = this.cache.ordersCache.getCachedOrder(productTimeSpan.timeSpan);

    if (orderLine) {
      this.cache.timeStampsCache.productTimeSpans.forEach(
        productTimeSpanOther => {
          if (productTimeSpanOther.timeSpan !== productTimeSpan.timeSpan && this.areaItems) {

            const originalAreaItem = this.areaItems.find(a =>
              a.productId === orderLine.productId);

            const areaItem = this.areaItems.find(a =>
              a.name === originalAreaItem.name
              &&
              a.group === originalAreaItem.group
              &&
              a.minutes === productTimeSpanOther.timeSpan);

            if (areaItem) {
              const orderOther = this.cache.ordersCache.getOrCreateCachedOrder(productTimeSpanOther.timeSpan);
              const price = this.bookingOrderService.computePriceByAreaItem(areaItem, orderLine.quantity);

              const orderLineOther = orderOther.orderLines.find(l => l.productId === areaItem.productId);
              if (!orderLineOther) {
                orderOther.addOrderLine(
                  orderLineType,
                  areaItem.productName,
                  areaItem.productId,
                  price,
                  orderLine.taxRate,
                  orderLine.quantity,
                  orderLine.readonly,
                  null,
                  orderLine.groupSize,
                );
                this.cache.ordersCache.updateServiceOrderLinesFromCache(productTimeSpanOther.timeSpan);
              } else {
                orderLineOther.quantity = orderLine.quantity;
                orderLineOther.price = price;
                orderLineOther.groupSize = orderLine.groupSize;
              }

              this.updateTimeSpan(orderOther, productTimeSpanOther).subscribe(done => {
                this.cache.ordersCache.updateServiceOrderLinesFromCache(productTimeSpanOther.timeSpan);
                this.updateInvalidReason();
              });
            }
          }
        }
      );
    }

    this.updateTimeSpan(
      order,
      productTimeSpan
    ).subscribe(
      done => {
        this.countUpEnabled = productTimeSpan.isCapacityAvailable;
        this.cache.ordersCache.updateServiceOrderLinesFromCache(productTimeSpan.timeSpan, true);
        this.orderStateService.orderSaveToLocalStorage();
        this.updateInvalidReason();
      }
    );
  }

  updateInvalidReason() {
    const invalidReasons = Array<string>();
    this._productPacks.forEach(x => { x.hasError = false; });

    for (const productPack of this._productPacks) {
      if (productPack.error) {
        invalidReasons.push(productPack.error);
        break;
      }
    }

    this.fillGroupInvalidReason(invalidReasons);

    this.bookingOrderService.singleCache.updateInvalidReason(invalidReasons);
  }

  hasErrorx(productPack: ProductPack): boolean {
    return true;
  }

  fillGroupInvalidReason(invalidReasons: string[]) {
    const groupToCount: Map<string, number> = new Map<string, number>();
    const groupToPacks: Map<string, ProductPack[]> = new Map<string, ProductPack[]>();

    for (const productPack of this._productPacks) {
      for (const c of productPack.counters) {
        if (!this.counterToGroup.has(c.id) || c.count === 0) {
          continue;
        }

        const groupName = this.counterToGroup.get(c.id);
        let currentGroupCount = 0;
        if (groupToCount.has(groupName)) {
          currentGroupCount = groupToCount.get(groupName);
        }

        currentGroupCount += c.count;
        groupToCount.set(groupName, currentGroupCount);

        const currentGroupPacks = groupToPacks.has(groupName) ? groupToPacks.get(groupName) : [];
        currentGroupPacks.push(productPack);
        groupToPacks.set(groupName, currentGroupPacks);
      }
    }

    for (const groupName of Array.from(groupToCount.keys())) {
      if (!this.groupToMinValue.has(groupName)) {
        continue;
      }

      const minValue = this.groupToMinValue.get(groupName);
      if (minValue <= 0) {
        continue;
      }

      const count = groupToCount.get(groupName);
      if (count < minValue) {
        invalidReasons.push(
          this.translateService.instant(
            'Minimum number of persons for the group',
            {
              group: this.translateService.instant(groupName),
              count: minValue
            }
          )
        );
        const packs = groupToPacks.get(groupName);
        if (packs) {
          packs.forEach(x => { x.hasError = true; });
        }
      }
    }
  }

  get labelWithIconData() {
    return this._labelWithIconData;
  }

  get productPacks() {
    return this._productPacks;
  }

  private updateProductPackMaxCount() {
    let singleProductCount = 0;
    const minutesToAvailableCount: Map<number, number> = new Map<number, number>();

    for (const productPack of this.productPacks) {
      for (const c of productPack.counters) {
        if (this.singleProductIds.has(c.id)) {
          singleProductCount += c.count;
        }

        this.fillAvailableCount(c, minutesToAvailableCount);
      }
    }

    for (const productPack of this.productPacks) {
      for (const c of productPack.counters) {
        if (!this.hasAvailableCount(c, minutesToAvailableCount)) {
          c.disableUp = true;
          continue;
        }

        if (this.singleProductIds.has(c.id)) {
          c.disableUp = singleProductCount >= this.totalSingleCountMax;
        } else {
          c.disableUp = false;
        }
      }
    }
  }

  private fillAvailableCount(counter: Counter, minutesToAvailableCount: Map<number, number>) {
    if (minutesToAvailableCount == null || !counter || !this.counterToMinutes.has(counter.id)) {
      return;
    }

    const minutes = this.counterToMinutes.get(counter.id);
    let availableCount = 0;
    if (minutesToAvailableCount.has(minutes)) {
      availableCount = minutesToAvailableCount.get(minutes) - counter.count;
    } else {
      if (this.minutesToMaxCount.has(minutes)) {
        availableCount = this.minutesToMaxCount.get(minutes) - counter.count;
      }
    }
    minutesToAvailableCount.set(minutes, availableCount);
  }

  private hasAvailableCount(counter: Counter, minutesToAvailableCount: Map<number, number>): boolean {
    if (minutesToAvailableCount == null || !counter || !this.counterToMinutes.has(counter.id)) {
      return false;
    }

    const counterMinutes = this.counterToMinutes.get(counter.id);

    for (const minutes of Array.from(minutesToAvailableCount.keys())) {
      if (minutes > counterMinutes) {
        continue;
      }

      const availableCount = minutesToAvailableCount.get(minutes);

      if (availableCount <= 0) {
        return false;
      }
    }

    return true;
  }

  get isVUMode(): boolean {
    return this.shopSettingsService.isVUMode;
  }

  get pageCount(): number {
    return Math.ceil(this.productPacks.length / this.productPacksPerPage);
  }

  get productPacksPerPages(): ProductPack[] {
    if (!this.productPacksPerPage) {
      return this.productPacks;
    }

    let currentPageIndex = this.currentPageIndex;
    if (currentPageIndex > this.pageCount) {
      currentPageIndex = this.pageCount;
    }
    const start = this.productPacksPerPage * currentPageIndex;
    let end = start + this.productPacksPerPage;
    if (end >= this.productPacks.length) {
      end = this.productPacks.length;
    }

    return this.productPacks.slice(start, end);
  }

  onPrevPage() {
    if (this.currentPageIndex <= 0) {
      return;
    }

    this.currentPageIndex--;
  }

  onNextPage() {
    if ((this.currentPageIndex + 1) >= this.pageCount) {
      return;
    }

    this.currentPageIndex++;
  }

  get isShowPageNavigationBar(): boolean {
    return this.productPacksPerPage && this.productPacks.length > this.productPacksPerPage;
  }

  get isDisablePerPageButton(): boolean {
    return this?.currentPageIndex <= 0;
  }

  get isDisableNextPageButton(): boolean {
    return (this.currentPageIndex + 1) >= this.pageCount;
  }
}
